/**
 * Customer management type definitions
 */

export enum CustomerType {
  RETAIL = 'RETAIL',
  RESELLER = 'RESELLER',
  WHOLESALER = 'WHOLESALER',
  BROKER = 'BROKER'
}

export interface Address {
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  is_default: boolean;
}

export interface TransportDetails {
  transport_name?: string;
  transport_contact?: string;
  transport_gstin?: string;
}

export interface CommunicationPreferences {
  email_enabled: boolean;
  sms_enabled: boolean;
  whatsapp_enabled: boolean;
  notification_types: {
    order_updates: boolean;
    payment_reminders: boolean;
    promotional: boolean;
  };
}

export interface CustomerGroup {
  id: string;
  name: string;
  description?: string;
  rules: Record<string, any>;
  default_credit_limit: number;
  default_credit_days: number;
  moq_rules: Record<string, any>;
  moa_rules: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Customer {
  id: string;
  name: string;
  billing_name: string;
  display_name: string;  // Computed field for dropdowns
  email: string;
  mobile_no: string;
  phone?: string;
  whatsapp_no?: string;
  contact_person?: string;
  contact_person_designation?: string;
  contact_person_email?: string;
  gstin?: string;
  pan?: string;
  is_gst_registered: boolean;
  customer_type: CustomerType;
  customer_group_id: string;
  credit_limit: number;
  credit_days: number;
  transport_option?: string;
  transport_details: Record<string, any>;
  assigned_catalogues: string[];
  communication_preferences: CommunicationPreferences;
  meta: Record<string, any>;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  customer_group?: CustomerGroup;
  billing_address?: Address;
  shipping_addresses?: Address[];
}

export interface CustomerCreateData {
  name: string;
  billing_name: string;
  email: string;
  mobile_no: string;
  phone?: string;
  whatsapp_no?: string;
  contact_person?: string;
  contact_person_designation?: string;
  contact_person_email?: string;
  gstin?: string;
  pan?: string;
  is_gst_registered: boolean;
  customer_type: CustomerType;
  customer_group_id: string;
  credit_limit?: number;
  credit_days?: number;
  transport_option?: string;
  transport_details?: Record<string, any>;
  assigned_catalogues?: string[];
  communication_preferences?: CommunicationPreferences;
  meta?: Record<string, any>;
  password?: string;
  billing_address?: Address;
  shipping_addresses?: Address[];
}

export interface CustomerUpdateData {
  name?: string;
  billing_name?: string;
  email?: string;
  mobile_no?: string;
  phone?: string;
  whatsapp_no?: string;
  contact_person?: string;
  contact_person_designation?: string;
  contact_person_email?: string;
  gstin?: string;
  pan?: string;
  is_gst_registered?: boolean;
  customer_type?: CustomerType;
  customer_group_id?: string;
  credit_limit?: number;
  credit_days?: number;
  transport_option?: string;
  transport_details?: Record<string, any>;
  assigned_catalogues?: string[];
  communication_preferences?: CommunicationPreferences;
  meta?: Record<string, any>;
  is_active?: boolean;
  password?: string;
  billing_address?: Address;
  shipping_addresses?: Address[];
}

export interface CustomerGroupCreateData {
  name: string;
  description?: string;
  rules?: Record<string, any>;
  default_credit_limit?: number;
  default_credit_days?: number;
  moq_rules?: Record<string, any>;
  moa_rules?: Record<string, any>;
  is_active?: boolean;
}

export interface CustomerGroupUpdateData {
  name?: string;
  description?: string;
  rules?: Record<string, any>;
  default_credit_limit?: number;
  default_credit_days?: number;
  moq_rules?: Record<string, any>;
  moa_rules?: Record<string, any>;
  is_active?: boolean;
}

export interface CustomerListResponse {
  items: Customer[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface CustomerGroupListResponse {
  items: CustomerGroup[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface CustomerTerms {
  customer_id: string;
  customer_name: string;
  customer_type: string;
  credit_limit: number;
  credit_days: number;
  group_name?: string;
  group_rules: Record<string, any>;
  moq_rules: Record<string, any>;
  moa_rules: Record<string, any>;
  assigned_catalogues: string[];
  communication_preferences: CommunicationPreferences;
}

export interface CustomerFilters {
  customer_type?: CustomerType;
  customer_group_id?: string;
  is_active?: boolean;
  search?: string;
  name?: string;
  email?: string;
  mobile?: string;
  gstin?: string;
}

export interface PricingTier {
  id: string;
  name: string;
  rules: Record<string, any>;
  margin_percent: number;
  margin_amount: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PricingTierCreateData {
  name: string;
  rules?: Record<string, any>;
  margin_percent?: number;
  margin_amount?: number;
  is_active?: boolean;
}

export interface PricingTierUpdateData {
  name?: string;
  rules?: Record<string, any>;
  margin_percent?: number;
  margin_amount?: number;
  is_active?: boolean;
}